import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast, Toaster } from "sonner";
import { Pill, MessageCircle, CheckCircle2, ArrowRight, Phone, Package, Trash2 } from "lucide-react";
import { CLINIC } from "../lib/clinic";
import { openWhatsApp } from "../lib/whatsapp";

export default function Refills() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/refills?days_ahead=7");
      setRows(r.data.refills || []);
    } catch { toast.error("Failed to load refills"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const whatsappRefill = async (row) => {
    const endStr = row.supply_end ? new Date(row.supply_end).toLocaleDateString(undefined, { day: "2-digit", month: "long" }) : "";
    const msg = `Dear ${row.patient_name}, your current medicines${row.driver_medicine ? " (esp. " + row.driver_medicine + ")" : ""} will run out around ${endStr}. Please contact ${CLINIC.name} in advance for a refill or follow-up.`;
    openWhatsApp(row.mobile, msg);
    try {
      await api.post(`/refills/${row.prescription_id}/refilled`, { channel: "whatsapp" });
      setRows((prev) => prev.map((r) => r.prescription_id === row.prescription_id ? { ...r, refilled: true } : r));
      toast.success("Marked as refilled");
    } catch { toast.error("Failed to update"); }
  };

  const markManual = async (row) => {
    try {
      await api.post(`/refills/${row.prescription_id}/refilled`, { channel: "manual" });
      setRows((prev) => prev.map((r) => r.prescription_id === row.prescription_id ? { ...r, refilled: true } : r));
      toast.success("Marked as refilled");
    } catch { toast.error("Failed"); }
  };

  const dismiss = async (row) => {
    if (!confirm(`Remove ${row.patient_name}'s refill entry from this list? The prescription itself is not affected.`)) return;
    try {
      await api.delete(`/refills/${row.prescription_id}`);
      setRows((prev) => prev.filter((r) => r.prescription_id !== row.prescription_id));
      toast.success("Refill entry removed");
    } catch { toast.error("Failed to remove"); }
  };

  const pending = rows.filter((r) => !r.refilled).length;

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="overline">SUPPLY MONITORING</div>
      <h1 className="font-heading text-3xl font-semibold mt-1 text-slate-900 flex items-center gap-3">
        <Pill size={26} className="text-pink-600" /> Medication refills
      </h1>
      <p className="mt-2 text-sm text-slate-600 max-w-xl">
        Patients whose current prescription supply is running out — or has already run out — in the next 7 days.
        Send a proactive WhatsApp so they don't run out of essential medicines.
      </p>

      <div className="grid grid-cols-2 gap-4 mt-6 max-w-md">
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="refills-pending">
          <div className="overline">Pending</div>
          <div className="font-heading text-3xl font-semibold mt-1 tabular text-red-600">{pending}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="overline">Handled</div>
          <div className="font-heading text-3xl font-semibold mt-1 tabular text-emerald-600">{rows.length - pending}</div>
        </div>
      </div>

      <div className="mt-6 space-y-2" data-testid="refills-list">
        {loading && <div className="text-sm text-slate-500 p-4">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded-md p-12 text-center bg-white">
            <Package size={26} className="text-slate-300 mx-auto" />
            <div className="mt-3 text-sm font-medium text-slate-700">No refills pending</div>
            <div className="mt-1 text-xs text-slate-500">Prescriptions running out in the next 7 days will appear here automatically.</div>
          </div>
        )}
        {rows.map((r) => {
          const days = r.days_until_out;
          const label =
            days === 0 ? "Out today" :
            days < 0 ? `${Math.abs(days)}d overdue` :
            days === 1 ? "Runs out tomorrow" :
            `${days} days left`;
          const tone = days <= 0 ? "text-red-600" : days <= 3 ? "text-amber-600" : "text-emerald-700";
          return (
            <div key={r.prescription_id} className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-4" data-testid={`refill-row-${r.prescription_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/patient/${r.patient_id}`} className="font-medium text-slate-900 hover:text-pink-700">
                    {r.patient_name}
                  </Link>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold tabular">{r.patient_id}</span>
                  {r.refilled && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`refilled-badge-${r.prescription_id}`}>
                      <CheckCircle2 size={10} /> Refilled
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                  {r.mobile && <span className="flex items-center gap-1 tabular"><Phone size={11} /> {r.mobile}</span>}
                  {r.diagnosis && <span>Dx: {r.diagnosis}</span>}
                </div>
                <div className="mt-1 text-xs flex items-center gap-3">
                  <span className="text-slate-700">
                    <Pill size={11} className="inline mr-1 text-pink-500" />
                    {r.driver_medicine || "—"} · {r.supply_days} day supply
                  </span>
                  <span className={`text-[11px] font-semibold uppercase tracking-wider ${tone}`}>{label}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => whatsappRefill(r)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 gap-2" data-testid={`whatsapp-refill-${r.prescription_id}`}>
                  <MessageCircle size={14} /> WhatsApp
                </Button>
                {!r.refilled && (
                  <button onClick={() => markManual(r)} className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1" data-testid={`refill-mark-${r.prescription_id}`}>
                    Mark refilled
                  </button>
                )}
                <button onClick={() => dismiss(r)} className="p-2 rounded hover:bg-red-50 text-red-500" title="Remove from list" data-testid={`refill-dismiss-${r.prescription_id}`}>
                  <Trash2 size={14} />
                </button>
                <Link to={`/patient/${r.patient_id}`} className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Open patient">
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
